import { useLayoutEffect, useRef, useState } from "react";

export const useCradSize = ({
  min,
  max,
  gap,
  aspectRatio = 16 / 9,
  padding = 0,
  resizeObserverListen = true,
}: {
  min: number;
  max: number;
  aspectRatio?: number;
  gap: number;
  padding?: number;
  resizeObserverListen?: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const rafRef = useRef<number | null>(null);
  const [columns, setColumns] = useState(1);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    resize();
    // 监听窗口变化
    window.addEventListener("resize", debouncedResize);
    if (!resizeObserverListen) {
      return () => {
        window.removeEventListener("resize", debouncedResize);
      };
    }
    if (ref.current && typeof ResizeObserver !== "undefined") {
      const resizeObserver = new ResizeObserver(() => {
        debouncedResize();
      });
      resizeObserver.observe(ref.current);
      return () => {
        window.removeEventListener("resize", debouncedResize);
        resizeObserver?.disconnect();
      };
    }
  }, [ref.current]);

  const resize = () => {
    if (ref.current) {
      let columns = 1;
      while (columns < 20) {
        const containerRect = ref.current.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const columnWidth = containerWidth / columns;
        if (columnWidth > min + gap && columnWidth < max + gap) {
          break;
        }
        columns++;
      }
      if (columns >= 20) {
        columns = 1;
      }
      const width = Math.min((ref.current.offsetWidth - padding) / columns - gap, max);
      setColumns(columns);
      setWidth(width);
      setHeight((width * aspectRatio));
    }
  };

  const debouncedResize = () => {
    // 清除之前的定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    // 设置防抖延迟
    debounceTimerRef.current = setTimeout(() => {
      // 使用requestAnimationFrame确保在下一帧执行
      rafRef.current = requestAnimationFrame(() => {
        resize();
      });
    }, 10); // 50ms防抖延迟
  };

  return { ref, columns, width, height };
};
